import { ELEMENT_NODE, TEXT_NODE } from '@qdabra/forms-shared/nodeTypes';
import { includes } from 'ramda';
import createElement from 'virtual-dom/create-element';
import diff from 'virtual-dom/diff';
import h from 'virtual-dom/h';
import patch from 'virtual-dom/patch';
import SoftSetHook from 'virtual-dom/virtual-hyperscript/hooks/soft-set-hook';
import autosize from '../../../Libraries/autosize';
import { CSS_MIME_TYPE } from '../../utility/mime.helpers';
import { xmlStringToBoolean } from '../../utility/xmlHelpers';
import setupNewPeoplePickerUi from '../UI/peoplePickerComponent';
import getPersonValuesByNodeId from '../helpers/peoplePicker.helpers';
import qdDateFormatter from '../qdDateFormatter';
import makeRichTextManager from '../richTextManager';
import { ATTR_FV_DS, ATTR_FV_ID } from '../view/view.attribute.names';
import {
    VIEW_EVENT_AFTER_INITIAL_RENDER,
    VIEW_EVENT_AFTER_RENDER,
    VIEW_EVENT_AFTER_VIEW_NAME_CHANGE,
    VIEW_EVENT_BEFORE_INITIAL_RENDER,
    VIEW_EVENT_BEFORE_RENDER,
} from '../view/viewEventTypes';
import { getNodeFormatAttr } from '../viewWatcher';
import { applyAutoCompleteSettings } from './viewManager.autoComplete';

const $ = window.jQuery;
const qd = window.qd;
const { compile: compileXslt } = qd.xslt;
const { perfMark, perfMeasure } = qd.util;

// Keeps track of the current view, the view templates, and
// carries out the rendering of the view on the page
const dateFormatter = qdDateFormatter();

export const eventTypes = {
    afterInitialRender: VIEW_EVENT_AFTER_INITIAL_RENDER,
    afterRender: VIEW_EVENT_AFTER_RENDER,
    beforeInitialRender: VIEW_EVENT_BEFORE_INITIAL_RENDER,
    beforeRender: VIEW_EVENT_BEFORE_RENDER,
    afterViewNameChange: VIEW_EVENT_AFTER_VIEW_NAME_CHANGE,
};

function viewManager(template, renderTarget, dataSources, logging, settings) {
    let viewDefinitions = {};
    let currentView;
    let lastRendered;
    let rootNode;
    let eventListeners = {
        afterInitialRender: [],
        afterRender: [],
        beforeInitialRender: [],
        beforeRender: [],
        afterViewNameChange: [],
    };
    let isFirstRender = true;

    const richTextManager = makeRichTextManager();

    function setTarget(target) {
        renderTarget = $(target);
    }

    function convertTo12Hr(digit) {
        if (digit === 0) {
            return 12;
        }

        if (digit < 13) {
            return digit;
        }

        return digit - 12;
    }

    function convertToTwoDigit(digit) {
        var prefix = digit >= 10 ? '' : '0';

        return prefix + digit;
    }

    function utcTimeString(date) {
        var utcHours = date.getHours(),
            isAM = utcHours < 12;
        utcHours = convertToTwoDigit(convertTo12Hr(utcHours));

        var utcMinutes = convertToTwoDigit(date.getMinutes()),
            utcSeconds = convertToTwoDigit(date.getSeconds());

        return String.format(
            '{0}:{1}:{2} {3}',
            utcHours,
            utcMinutes,
            utcSeconds,
            isAM ? 'AM' : 'PM',
        );
    }

    function formatDateString(dateString) {
        if (!dateString) {
            return null;
        }

        if (dateString.length > 19) {
            dateString = dateString.substring(0, 19);
        }

        var timeRegEx = /T\d{2}:\d{2}:\d{2}$/;

        if (dateString && !timeRegEx.test(dateString)) {
            dateString += 'T00:00:00';
        }

        var endDateString = dateString[dateString.length - 1];
        if (endDateString !== 'Z' || endDateString !== 'z') {
            dateString += 'Z';
        }

        return dateString;
    }

    function getDateFormat(el) {
        const fmtAttrList = getNodeFormatAttr(el);

        if (!fmtAttrList) {
            return null;
        }

        return dateFormatter.getDatePickerFormatOptions(fmtAttrList);
    }

    function setDatePickerFormat($el) {
        const elFormat = getDateFormat($el[0]);

        if (!elFormat) {
            return;
        }

        const regionalFormat = dateFormatter.getDatePickerRegionalFormat(
            elFormat.locale,
        );

        if (regionalFormat) {
            $el.datepicker('option', regionalFormat);

            if (elFormat.dateFormat) {
                $el.datepicker('option', 'dateFormat', elFormat.dateFormat);
            }
        }
    }

    function enableDisableDatePicker($el) {
        var contentEdit = $el.attr('contenteditable');

        if (contentEdit !== 'false') {
            if ($el.attr('disabled')) {
                $el.removeAttr('disabled');
            }
        } else {
            if (!$el.attr('disabled')) {
                $el.attr('disabled', 'disabled');
            }
        }
    }

    function addDatePickers() {
        //changeMonth, changeYear: allow user to select month and year.

        $.datepicker.setDefaults($.datepicker.regional['']);

        renderTarget
            .find('.xdDTPicker > input')
            .datepicker({
                showOn: 'button',
                changeMonth: true,
                changeYear: true,
                buttonImage: '/images/calendar.gif',
                buttonImageOnly: true,
                buttonText: 'Select date',
                beforeShow: function (input) {
                    if (!$(input).prop('readOnly')) {
                        $(input).data('disableMobileKeyboard', true);
                        $(input).prop('readOnly', true);
                    }
                    var contentEdit = $(input).attr('contenteditable');
                    return contentEdit !== 'false';
                },
                onClose: function (_, inst) {
                    if ($(inst.input).data('disableMobileKeyboard')) {
                        $(inst.input).prop('readOnly', false);
                        $(inst.input).data('disableMobileKeyboard', false);
                    }
                },
                //,
                //defaultDate: 0
                //,dateFormat: "yy-mm-dd" //Set incase we use FVUtil.parseXmlDateTime
            })
            .each(function (i, el) {
                var $el = $(el),
                    timeElement = $el
                        .parent()
                        .next('.xdTimeTextBoxWrap')
                        .find('input'),
                    hasTimeElement = timeElement.length > 0,
                    dateValue = $el.attr('data-fv-value'),
                    origValue = dateValue;

                setDatePickerFormat($el);
                enableDisableDatePicker($el);

                dateValue = formatDateString(dateValue);

                var dateParsed = dateValue ? Date.parse(dateValue) : null,
                    date =
                        dateParsed &&
                        dateParsed > 0 &&
                        !Number.isNaN(dateParsed)
                            ? new Date(dateParsed)
                            : null;

                //date = FVUtil.parseXmlDateTime($el.val());TODO:Check parse required ??

                if (!date) {
                    if (hasTimeElement) {
                        timeElement.val(origValue);
                    }
                    $el.val(origValue);
                    return;
                }

                date = new Date(
                    date.getTime() + date.getTimezoneOffset() * 60 * 1000,
                );
                $el.datepicker('setDate', date);

                if (hasTimeElement) {
                    timeElement.val(utcTimeString(date));
                }
            });
    }

    function addAutoSizeTextboxes() {
        var multilineBoxes = renderTarget
            .find('textarea')
            .css('white-space', 'pre-wrap')
            .filter('[data-auto-height = true]');

        // Case 47926 - Using update() to resize multiline boxes before each render
        autosize(multilineBoxes);
        autosize.update(multilineBoxes);
    }

    /// <summary>Function to render Richtext box control using CkEditor</summary>
    function addRichTextBox() {
        richTextManager.ensureRichText(renderTarget);
    }

    const setupCustomPeoplePicker = (node) => {
        const fvDs = node.getAttribute(ATTR_FV_DS);
        const fvId = node.getAttribute(ATTR_FV_ID);
        const isMulti = xmlStringToBoolean(node.getAttribute('allowMultiple'));
        const peoplePickerData = getPersonValuesByNodeId(
            dataSources,
            fvId,
            fvDs,
        );

        setupNewPeoplePickerUi(node, isMulti, peoplePickerData);
    };

    function addPeoplePickers($viewDom) {
        const pickerNodes = $viewDom.find('.ppl-picker').toArray();

        for (const node of pickerNodes) {
            setupCustomPeoplePicker(node);
        }
    }

    // 2024-03-07 - Removed unused modifyDefaultSelect()

    function makeAttrMap(attributes) {
        var attrs = {};

        Object.keys(attributes).forEach(function (key) {
            attrs[attributes[key].name] = attributes[key].value;
        });

        return attrs;
    }

    function findAttribute(rawEl, name) {
        return Object.values(rawEl.attributes).find(function (att) {
            return att.name === name;
        });
    }

    function hasAttribute(rawEl, name) {
        return !!findAttribute(rawEl, name);
    }

    function hasBlankValueAttribute(el) {
        var valAttr = findAttribute(el, 'value');

        return valAttr && valAttr.value === '';
    }

    function selectHasBlankOption(options) {
        return options.some(hasBlankValueAttribute);
    }

    function isOptionNode(rawEl) {
        return rawEl.name.toLowerCase() === 'option';
    }

    function isSelectedOption(rawEl) {
        return hasAttribute(rawEl, 'selected') && isOptionNode(rawEl);
    }

    function selectedIndex(rawNodes) {
        return Math.max(0, rawNodes.findIndex(isSelectedOption));
    }

    // BUGBUG - Could probably use a SoftSetHook instead of this
    function selectedIndexHook(index) {
        var hook = function () {};
        hook.prototype.hook = function (node) {
            node.selectedIndex = index;
        };
        return new hook();
    }

    function isOptionWithoutValueAttr(rawEl) {
        return !hasAttribute(rawEl, 'value') && isOptionNode(rawEl);
    }

    function addBlankValueAttribute(rawEl) {
        rawEl.attributes['{}value'] = {
            localName: 'value',
            name: 'value',
            namespaceUri: '',
            type: 2,
            value: '',
        };
    }

    function ensureValueAttributes(selectRawEl) {
        selectRawEl.children
            .filter(isOptionWithoutValueAttr)
            .forEach(addBlankValueAttribute);
    }

    function fixupSelect(rawNode, props) {
        var options = rawNode.children.filter(isOptionNode);

        // If the source options contain a blank value, the IP view XSL can cause the rendered <select> to have two <options> with blank values,
        // one of which lacks a value= attribute. In this situation, we want to exclude the one that lacks a <value> attribute as it messes with
        // the virtualdom
        if (selectHasBlankOption(options)) {
            rawNode.children = rawNode.children.filter(function (el) {
                return hasAttribute(el, 'value') || !isOptionNode(el);
            });
        }

        // Case 44820
        ensureValueAttributes(rawNode);

        props.selectedIndex = selectedIndexHook(
            selectedIndex(rawNode.children),
        );
    }

    function renderVNode(rawNode) {
        if (rawNode.type === ELEMENT_NODE) {
            var attributes = rawNode.attrMap || makeAttrMap(rawNode.attributes),
                props = { attributes: attributes },
                lowerName = rawNode.name.toLowerCase(),
                children;

            // case 41047 - changes to textbox value are not reflected in view
            // setting .value property on <select> elements can cause a blank value to be incorrectly selected
            if ('value' in attributes) {
                if (lowerName !== 'select') {
                    props.value = attributes.value;
                }
            }

            if (lowerName === 'select') {
                fixupSelect(rawNode, props);
            }

            if (
                lowerName === 'input' &&
                (attributes.type === 'checkbox' || attributes.type === 'radio')
            ) {
                // Case 50359
                props.checked = new SoftSetHook(
                    'checked' in attributes || 'CHECKED' in attributes,
                );
            }

            // case 41077 - multiline text box's value gets duplicated
            if (lowerName === 'textarea') {
                children = [];
                props.value = rawNode.children
                    .filter(function (n) {
                        return n.type === TEXT_NODE;
                    })
                    .map(function (n) {
                        return n.value;
                    })
                    .join('');
            } else {
                children = rawNode.children
                    .map(renderVNode)
                    .filter(function (n) {
                        return n !== null;
                    });
            }

            return h(rawNode.name, props, children);
        }
        if (rawNode.type === TEXT_NODE) {
            return rawNode.value;
        }
        return null;
    }

    function renderView(viewBody, formDom, functions) {
        logging.logVerbose('Begin view render');

        var result = viewBody.transformRaw(formDom, {
            functions: functions,
        });
        var tree = renderVNode(result[0]);

        if (lastRendered) {
            var patches = diff(lastRendered, tree);

            rootNode = patch(rootNode, patches);
        } else {
            rootNode = createElement(tree);
            renderTarget.empty();
            renderTarget.append(rootNode);
        }

        lastRendered = tree;

        addDatePickers();
        addRichTextBox();
        addPeoplePickers(renderTarget);
        addAutoSizeTextboxes();
        applyAutoCompleteSettings(renderTarget[0], settings);
        //modifyDefaultSelect();

        logging.logVerbose('End view render');
    }

    async function loadView(viewName) {
        logging.logVerbose(`Begin load view ${viewName}`);

        const { Head, Main } = await template.getViewDefinitionAsync(viewName);

        const body = $.parseXML(Main);

        logging.logVerbose(`Begin view compile ${viewName}`);

        const viewData = {
            head: $.parseXML(Head),
            body: compileXslt(body),
        };

        viewDefinitions[viewName] = viewData;

        logging.logVerbose(`End view load ${viewName}`);

        return viewData;
    }

    const getViewDefinition = async (viewName) =>
        viewDefinitions[viewName] ?? (await loadView(viewName));

    function removeStyles($pageHead) {
        $pageHead.children('style[data-creator=formsViewer]').remove();
    }

    function prepareStyle(element) {
        var ss1 = document.createElement('style'),
            def = element.textContent,
            tt1;

        ss1.setAttribute('type', CSS_MIME_TYPE);
        ss1.setAttribute('data-creator', 'formsViewer');

        if (ss1.styleSheet) {
            // IE
            ss1.styleSheet.cssText = def;
        } else {
            // others
            tt1 = document.createTextNode(def);
            ss1.appendChild(tt1);
        }

        return ss1;
    }

    function setStyles(viewHead) {
        const pageHead = document.head || $('head')[0];
        let $pageHead = $(pageHead);

        const styles = $(viewHead).find('style');

        removeStyles($pageHead);

        styles.each((_, el) => {
            $pageHead.append(prepareStyle(el));
        });
    }

    async function executeEventListeners(listeners) {
        for (const listener of listeners) {
            try {
                await listener();
            } catch (e) {
                console.error(e);
            }
        }
    }

    function afterRenderPerfMarks() {
        perfMark('formsviewer_view_rendered');
        perfMeasure(
            'formsviewer_view_file',
            'formsviewer_view_render_start',
            'formsviewer_view_rendering',
        );
        perfMeasure(
            'formsviewer_view_render',
            'formsviewer_view_rendering',
            'formsviewer_view_rendered',
        );
    }

    async function checkAfterFirstRender() {
        if (!isFirstRender) {
            return;
        }

        isFirstRender = false;

        //44786 - Navigate to top after switch view.
        $(window).scrollTop(0);

        await executeEventListeners(eventListeners.afterInitialRender);
    }

    function afterRender() {
        return executeEventListeners(eventListeners.afterRender);
    }

    function handleError(error) {
        if (error && error.message) {
            error.userDisplayMessage =
                "An error occurred while rendering the page. This is usually due to an issue in the form template's view definition:\n\n" +
                error.message;
        }

        throw error;
    }

    // todo: don't require passing in functions?
    async function startRenderViewAsync(formDom, functions) {
        try {
            // intentionally not awaiting this promise so that it can load in the background while
            // event listeners are run
            const pLoadedViewDefinition = getViewDefinition(currentView);

            perfMark('formsviewer_view_render_start');

            if (isFirstRender) {
                await executeEventListeners(eventListeners.beforeInitialRender);
            }

            await executeEventListeners(eventListeners.beforeRender);

            const { head, body } = await pLoadedViewDefinition;

            try {
                perfMark('formsviewer_view_rendering');
                if (isFirstRender) {
                    setStyles(head);
                }
                renderView(body, formDom, functions);
            } finally {
                afterRenderPerfMarks();
            }

            await checkAfterFirstRender();

            await afterRender();
        } catch (e) {
            handleError(e);
        }
    }

    function getCurrentView() {
        return currentView;
    }

    // String -> Promise[void]
    const setCurrentView = async (viewName) => {
        const viewNames = template.getViews();

        if (!includes(viewName, viewNames)) {
            throw new Error('Cannot set view, invalid view.');
        }

        // don't change if we are already on this view
        if (viewName === currentView) {
            return;
        }

        // case 47545 - Ensure DOM is cleared out when rendering a new view
        lastRendered = null;
        currentView = viewName;

        //Set to true to execute beforeInitialRender, afterInitialRender events.
        isFirstRender = true;

        await executeEventListeners(eventListeners.afterViewNameChange);
    };

    /**
     * Add event listener to events.
     * @param {string} type Event name after which listener should be executed.
     * @param {function()} listener Function to be executed after event occurs.
     */
    function addEventListener(type, listener) {
        if (!type) {
            console.warn('Event type is required');
            return;
        }

        if (!listener) {
            console.warn('Listener is required to add event.');
            return;
        }

        if (typeof listener !== 'function') {
            console.warn('Unsupported listener passed to addEventListener.');
            return;
        }

        switch (type) {
            case VIEW_EVENT_AFTER_INITIAL_RENDER:
                eventListeners.afterInitialRender.push(listener);
                return;
            case VIEW_EVENT_AFTER_RENDER:
                eventListeners.afterRender.push(listener);
                return;
            case VIEW_EVENT_BEFORE_INITIAL_RENDER:
                eventListeners.beforeInitialRender.push(listener);
                return;
            case VIEW_EVENT_BEFORE_RENDER:
                eventListeners.beforeRender.push(listener);
                return;
            case VIEW_EVENT_AFTER_VIEW_NAME_CHANGE:
                eventListeners.afterViewNameChange.push(listener);
                return;
            default:
                console.warn(`Unsupported event type: ` + type);
                return;
        }
    }

    function currentViewName() {
        return currentView;
    }

    // 2024-03-08 - No longer setting initial view during instantiation because setCurrentView() is now async

    // interface
    return {
        addEventListener,
        currentViewName,
        getCurrentView,
        setCurrentView,
        startRenderViewAsync,
        setTarget,
    };
}

export default viewManager;
